import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from './store';
import wootConstants from 'dashboard/constants/globals';
import FacebookPagesAPI from '../api/facebookPages';

/**
 * Composable for posts listing and pagination logic with independent tab states.
 * Each tab (Facebook/Instagram) maintains its own state, pagination, and cache.
 *
 * @param {Object} config - Configuration object
 * @param {string} config.postType - Type of posts: 'feed', 'reels', 'sponsored', or 'mentions'
 * @param {boolean} config.sponsored - Whether posts are sponsored
 * @param {string} [config.sourceType] - Optional source type (e.g., 'MENTIONED_INSTAGRAM')
 * @returns {Object} Posts list composable state and methods
 */

// Helper function to format date for API
const formatDateForAPI = date => {
  if (!date) return null;

  // If it's already a string, return it
  if (typeof date === 'string') {
    // If it's in dd/MM/yyyy format, convert to ISO format
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
      const [day, month, year] = date.split('/');
      return `${year}-${month}-${day}T00:00:00Z`;
    }
    return date;
  }

  // If it's a Date object, convert to ISO date format (YYYY-MM-DD)
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00Z`;
  }

  return null;
};

export function usePostsList(config = {}) {
  const { postType = null, sponsored = false, sourceType = null } = config;

  const route = useRoute();
  const store = useStore();
  const vm = getCurrentInstance();
  const router = vm?.proxy?.$router || null;

  // Getters
  const uiFlags = useMapGetter('posts/getUIFlags');
  const pagesList = useMapGetter('facebookPages/getPages');

  /**
   * Initialize state from URL params or defaults
   * This ensures state is preserved when coming back from detail views
   */
  const initializeStateFromURL = () => {
    const params = route.query;

    return {
      tab: params.tab || 'Facebook',
      page: parseInt(params.page || '1', 10),
      sort: params.sort || wootConstants.POSTS_SORT_BY.NEWEST,
      actives: params.actives || 'all',
      search: params.search || '',
    };
  };

  const urlState = initializeStateFromURL();

  // Global state (shared across tabs) - initialized from URL
  const activeTab = ref(urlState.tab);
  const globalFilter = ref({
    actives: urlState.actives,
    sort: urlState.sort,
  });
  const globalSearchText = ref(urlState.search);

  // Independent state for each tab
  const tabStates = ref({
    facebook: {
      posts: [],
      currentPage: activeTab.value === 'Facebook' ? urlState.page : 1,
      totalCount: 0,
      totalPages: 0,
      perPage: 10,
      loaded: false,
      loading: false,
      countsLoaded: false,
      scrollPosition: 0,
    },
    instagram: {
      posts: [],
      currentPage: activeTab.value === 'Instagram' ? urlState.page : 1,
      totalCount: 0,
      totalPages: 0,
      perPage: 10,
      loaded: false,
      loading: false,
      countsLoaded: false,
      scrollPosition: 0,
    },
  });

  // Computed
  const folderPages = computed(() => {
    const folderLabel = route.query.folder_label;
    if (!folderLabel || !pagesList.value) return null;
    return pagesList.value.filter(page => page.label === folderLabel);
  });

  const currentTabKey = computed(() => activeTab.value.toLowerCase());

  const currentTabState = computed(() => tabStates.value[currentTabKey.value]);

  const currentPosts = computed(() => currentTabState.value?.posts || []);

  const hasNoPosts = computed(() => {
    return !currentTabState.value?.totalCount;
  });

  const isLoading = computed(() => {
    return currentTabState.value?.loading || uiFlags.value.isFetching;
  });

  // Build params for API calls for a specific platform
  const buildParams = platform => {
    const state = tabStates.value[platform];
    const startDate = formatDateForAPI(globalFilter.value.publicationDateStart);
    const endDate = formatDateForAPI(globalFilter.value.publicationDateEnd);

    return {
      pagination: {
        [platform]: {
          currentPage: state.currentPage,
          perPage: state.perPage,
        },
        // Send the other platform with page 1 to avoid unnecessary data
        [platform === 'facebook' ? 'instagram' : 'facebook']: {
          currentPage: 1,
          perPage: 1,
        },
      },
      message: globalSearchText.value,
      order: globalFilter.value.sort,
      actives: globalFilter.value.actives,
      ...(startDate && {
        publicationDateStart: startDate,
      }),
      ...(endDate && {
        publicationDateEnd: endDate,
      }),
      ...(postType && { post_type: postType }),
      ...(sponsored !== null && { sponsored }),
      ...(sourceType && { source_type: sourceType }),
      // Include folder_label so backend can handle folder pagination
      ...(route.query.folder_label && {
        folder_label: route.query.folder_label,
      }),
    };
  };

  // Update tab state with fetched data - ONLY for the specified platform
  const updateTabState = (platform, data) => {
    // Safety check: only update if platform is valid
    if (!platform || !tabStates.value[platform]) return;

    const state = tabStates.value[platform];
    if (data) {
      state.posts = data.posts || [];
      // ⚠️ DO NOT update currentPage here - it's already set locally before fetching
      // state.currentPage is set in handlePageChange before the API call
      state.totalCount = data.totalCount || 0;
      state.totalPages = data.totalPages || 0;
      state.perPage = data.perPage || 12;
      state.loaded = true;
      state.countsLoaded = true;
    }
    state.loading = false;
  };

  // Update only the counts without touching other state
  const updateTabCounts = (facebook, instagram) => {
    if (facebook !== undefined) {
      tabStates.value.facebook.totalCount = facebook;
      tabStates.value.facebook.countsLoaded = true;
    }
    if (instagram !== undefined) {
      tabStates.value.instagram.totalCount = instagram;
      tabStates.value.instagram.countsLoaded = true;
    }
  };

  // Fetch posts for a specific platform
  // Now that backend supports folder_label, we always use a single API call
  // The backend handles combining posts from multiple pages in a folder
  const fetchPostsForPlatform = async platform => {
    const state = tabStates.value[platform];
    state.loading = true;

    // FREEZE the other platform's count before any store updates
    const otherPlatform = platform === 'facebook' ? 'instagram' : 'facebook';
    const frozenCount = tabStates.value[otherPlatform].totalCount;
    const frozenPages = tabStates.value[otherPlatform].totalPages;

    // Capture the page we are requesting to guard against stale responses
    const requestedPage = tabStates.value[platform].currentPage;

    try {
      await store.dispatch('posts/get', {
        pageId: route.params.page_id,
        params: buildParams(platform),
      });

      // Extract data from store (it updates via mutation)
      const storeData = store.getters['posts/getPosts'];

      // If the current page changed since the request started, ignore this response (stale)
      if (tabStates.value[platform].currentPage !== requestedPage) {
        state.loading = false;
        return; // Do not update state based on an outdated response
      }

      // Update ONLY this specific platform with full data
      if (storeData && storeData[platform]) {
        updateTabState(platform, storeData[platform]);
      }

      // RESTORE the frozen count OR use the count from store if frozen was 0
      if (frozenCount > 0) {
        tabStates.value[otherPlatform].totalCount = frozenCount;
        tabStates.value[otherPlatform].totalPages = frozenPages;
      } else if (storeData?.[otherPlatform]?.totalCount > 0) {
        tabStates.value[otherPlatform].totalCount =
          storeData[otherPlatform].totalCount;
        tabStates.value[otherPlatform].totalPages =
          storeData[otherPlatform].totalPages || 0;
      }
    } catch (error) {
      state.loading = false;
      throw error;
    }
  };

  // Fetch counts for both platforms without loading full data
  const fetchCounts = async () => {
    try {
      const startDate = formatDateForAPI(
        globalFilter.value.publicationDateStart
      );
      const endDate = formatDateForAPI(globalFilter.value.publicationDateEnd);

      const params = {
        message: globalSearchText.value,
        order: globalFilter.value.sort,
        actives: globalFilter.value.actives,
        ...(startDate && {
          publicationDateStart: startDate,
        }),
        ...(endDate && {
          publicationDateEnd: endDate,
        }),
        ...(postType && { post_type: postType }),
        ...(sponsored !== null && { sponsored }),
        ...(sourceType && { source_type: sourceType }),
        ...(route.query.folder_label && {
          folder_label: route.query.folder_label,
        }),
      };

      const response = await FacebookPagesAPI.getPostsCounts(
        route.params.page_id,
        params
      );

      // Update only the counts, not the posts
      if (response.data) {
        updateTabCounts(response.data.facebook, response.data.instagram);
        return true; // Success
      }
      return false; // No data
    } catch (error) {
      return false; // Failed - don't update counts
    }
  };

  // Fetch current tab if not loaded or force refresh
  const fetchCurrentTab = async (force = false) => {
    const platform = currentTabKey.value;
    const state = tabStates.value[platform];

    // Only fetch if not loaded or forced
    if (!state.loaded || force) {
      await fetchPostsForPlatform(platform);
    }
  };

  // Fetch both tabs (initial load or filter/search change)
  const fetchBothTabs = async () => {
    // Fetch counts FIRST to get accurate totals for both platforms
    // This sets countsLoaded = true for both platforms
    await fetchCounts();

    // Reset loaded state for both tabs AFTER counts are set
    // This forces a refresh of the current tab's data
    tabStates.value.facebook.loaded = false;
    tabStates.value.instagram.loaded = false;

    // Then fetch current tab's full data
    // Only the active platform will be updated by updateTabState()
    // The inactive platform keeps its counts from fetchCounts()
    await fetchCurrentTab(true);
  };

  // Refresh Facebook and Instagram lists (each keeps its current page). Sequential
  // avoids racing posts/get global isFetching the same way as comments moderation.
  const refreshAllPostTabs = async () => {
    await fetchCounts();
    await fetchPostsForPlatform('facebook');
    await fetchPostsForPlatform('instagram');
  };

  // Reset pagination for all tabs
  const resetAllPagination = () => {
    tabStates.value.facebook.currentPage = 1;
    tabStates.value.instagram.currentPage = 1;
  };

  // Save scroll position for current tab
  const saveScrollPosition = () => {
    const platform = currentTabKey.value;
    const scrollPosition = window.scrollY || window.pageYOffset;
    tabStates.value[platform].scrollPosition = scrollPosition;

    // Also persist to sessionStorage for cross-session recovery
    const key = `scroll_${route.params.page_id}_${platform}`;
    sessionStorage.setItem(key, scrollPosition.toString());
  };

  // Restore scroll position for current tab
  const restoreScrollPosition = () => {
    const platform = currentTabKey.value;

    // First try to get from sessionStorage (more reliable)
    const key = `scroll_${route.params.page_id}_${platform}`;
    const savedPosition = sessionStorage.getItem(key);
    let scrollPosition = savedPosition
      ? parseInt(savedPosition, 10)
      : tabStates.value[platform].scrollPosition || 0;

    window.scrollTo({
      top: scrollPosition,
      left: 0,
      behavior: 'instant',
    });
  };

  // Sync state to URL params for persistence
  const syncStateToURL = async () => {
    if (!router) {
      return; // Router not available
    }

    const platform = currentTabKey.value;
    const state = tabStates.value[platform];

    const newQuery = {
      ...route.query,
      tab: activeTab.value,
      page: state.currentPage.toString(),
      sort: globalFilter.value.sort,
      actives: globalFilter.value.actives,
      ...(globalSearchText.value && { search: globalSearchText.value }),
    };

    // Remove empty query params to keep URL clean
    Object.keys(newQuery).forEach(key => {
      if (!newQuery[key] || newQuery[key] === '') {
        delete newQuery[key];
      }
    });

    // Only update if query actually changed
    const queryChanged =
      JSON.stringify(newQuery) !== JSON.stringify(route.query);

    if (queryChanged && router) {
      try {
        await router.push({
          name: route.name,
          params: route.params,
          query: newQuery,
        });
      } catch (err) {
        // Ignore navigation errors (e.g., same route)
      }
    }
  };

  // Event handlers
  const handleToggleActive = async postId => {
    await store.dispatch('posts/toggleActive', {
      pageId: route.params.page_id,
      postId,
    });

    // Update local state optimistically in current tab
    const post = currentPosts.value.find(p => p.id === postId);
    if (post) {
      post.active = !post.active;
    }

    // Refresh counts after toggle (if filter is by active/inactive)
    if (globalFilter.value.actives !== 'all') {
      await fetchCounts();
    }
  };

  const handleFilterChange = async newFilter => {
    globalFilter.value = newFilter;
    resetAllPagination();
    syncStateToURL();
    await fetchBothTabs();
  };

  const handleSearch = async searchText => {
    globalSearchText.value = searchText;
    resetAllPagination();
    syncStateToURL();
    await fetchBothTabs();
  };

  const handlePageChange = async data => {
    const platform = data.tab.toLowerCase();
    const newPage = data.page;

    // Validar que a página está dentro dos limites válidos
    const totalPages = tabStates.value[platform].totalPages;
    if (newPage < 1 || (totalPages > 0 && newPage > totalPages)) {
      return; // Não fazer nada se a página for inválida
    }

    // Save scroll position before changing page
    saveScrollPosition();

    // Update local state FIRST
    tabStates.value[platform].currentPage = newPage;
    activeTab.value = data.tab;

    // Sync URL (non-blocking - we don't wait for navigation)
    syncStateToURL();

    // Fetch posts for the new page
    await fetchPostsForPlatform(platform);

    // Scroll to top when page changes
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    tabStates.value[platform].scrollPosition = 0;
  };

  const handleTabChange = async newTab => {
    // Save scroll position of current tab before switching
    saveScrollPosition();

    activeTab.value = newTab;

    // Sync URL (non-blocking)
    syncStateToURL();

    // Lazy load: only fetch if tab hasn't been loaded yet
    await fetchCurrentTab();

    // Restore scroll position after tab content is loaded
    restoreScrollPosition();
  };

  // Track previous values to detect actual context changes
  let previousPageId = route.params.page_id;
  let previousFolderLabel = route.query.folder_label;

  // Watchers
  // Only reset and refetch when page_id or folder_label ACTUALLY changes
  // Not on tab/page/filter/sort changes as those are handled by handlers
  watch(
    () => ({
      pageId: route.params.page_id,
      folderLabel: route.query.folder_label,
    }),
    newVal => {
      // Only reset if the page context actually changed
      const pageIdChanged = newVal.pageId !== previousPageId;
      const folderLabelChanged = newVal.folderLabel !== previousFolderLabel;

      if (pageIdChanged || folderLabelChanged) {
        previousPageId = newVal.pageId;
        previousFolderLabel = newVal.folderLabel;
        resetAllPagination();
        fetchBothTabs();
      }
    },
    { deep: true }
  );

  // Return computed state that matches the old structure for backward compatibility
  const posts = computed(() => ({
    facebook: {
      posts: tabStates.value.facebook.posts,
      currentPage: tabStates.value.facebook.currentPage,
      totalCount: tabStates.value.facebook.totalCount,
      totalPages: tabStates.value.facebook.totalPages,
      perPage: tabStates.value.facebook.perPage,
    },
    instagram: {
      posts: tabStates.value.instagram.posts,
      currentPage: tabStates.value.instagram.currentPage,
      totalCount: tabStates.value.instagram.totalCount,
      totalPages: tabStates.value.instagram.totalPages,
      perPage: tabStates.value.instagram.perPage,
    },
  }));

  return {
    // State (backward compatible)
    posts,
    uiFlags,
    pagesList,
    message: globalSearchText,
    filter: globalFilter,
    tab: activeTab,
    folderPages,

    // New state
    currentPosts,
    currentTabState,
    hasNoPosts,
    isLoading,
    tabStates,

    // Methods
    fetchPosts: fetchBothTabs,
    refreshAllPostTabs,
    fetchCurrentTab,
    fetchCounts,
    handleToggleActive,
    handleFilterChange,
    handleSearch,
    handlePageChange,
    handleTabChange,
    syncStateToURL,
  };
}
